"use client";

import Link from "next/link";
import { attr, BOX_ATTR, groupCart, type CartBox } from "@/lib/box";
import { formatMoney, variantLabel } from "@/lib/format";
import type { CartLine } from "@/lib/shopify/types";
import { ProductImage } from "@/components/ui/ProductImage";
import { QuantityStepper } from "@/components/ui/QuantityStepper";
import { useCart } from "./CartProvider";

export function CartLines({ onNavigate }: { onNavigate?: () => void }) {
  const { cart } = useCart();
  if (!cart) return null;
  const { boxes, singles } = groupCart(cart.lines);

  return (
    <ul className="divide-y divide-paper/20">
      {boxes.map((box) => (
        <BoxRow key={box.id} box={box} onNavigate={onNavigate} />
      ))}
      {singles.map((line) => (
        <LineRow key={line.id} line={line} onNavigate={onNavigate} />
      ))}
    </ul>
  );
}

function BoxRow({ box, onNavigate }: { box: CartBox; onNavigate?: () => void }) {
  const { removeLines, pending } = useCart();
  const title = box.collection?.title ?? (box.recipient ? `Custom box for ${box.recipient}` : "Custom box");
  const ribbon = box.box ? variantLabel(box.box.merchandise.title) : null;
  const contents = [...box.items, ...(box.card ? [box.card] : [])];

  return (
    <li className="flex gap-4 py-5">
      {box.box ? (
        <ProductImage
          image={box.box.merchandise.image}
          alt={title}
          sizes="96px"
          className="aspect-[4/5] w-24 shrink-0 self-start rounded-[4px]"
        />
      ) : (
        <Contents lines={box.items} title={title} />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-display text-xl leading-snug">
            {box.collection ? (
              <Link href={`/collections/${box.collection.handle}`} onClick={onNavigate} className="hover:underline">
                {title}
              </Link>
            ) : (
              title
            )}
          </h3>
          <p className="shrink-0 tabular-nums">{formatMoney(box.total)}</p>
        </div>
        {box.box ? (
          <p className="text-[0.9375rem] text-paper">
            {box.box.merchandise.product.title}
            {ribbon ? `, ${ribbon.toLowerCase()} ribbon` : ""}
          </p>
        ) : null}
        <ul className="mt-2 space-y-0.5 text-[0.9375rem]">
          {contents.map((line) => {
            const variant = variantLabel(line.merchandise.title);
            return (
              <li key={line.id} className="flex justify-between gap-3">
                <span className="truncate">
                  {line.quantity > 1 ? `${line.quantity} × ` : ""}
                  {line.merchandise.product.title}
                  {variant ? ` (${variant})` : ""}
                </span>
              </li>
            );
          })}
        </ul>
        {box.message ? (
          <p className="mt-3 line-clamp-3 border-l-2 border-gold/60 pl-3 font-serif text-[0.9375rem] italic text-paper">
            {box.message}
          </p>
        ) : null}
        <button
          type="button"
          className="link mt-3 text-sm disabled:opacity-50"
          onClick={() => removeLines(box.lineIds)}
          disabled={pending}
        >
          Remove box
        </button>
      </div>
    </li>
  );
}

// Our gift boxes have no packaging product to show, so the tile shows what's inside.
function Contents({ lines, title }: { lines: CartLine[]; title: string }) {
  if (lines.length < 4) {
    return (
      <ProductImage
        image={lines[0]?.merchandise.image}
        alt={title}
        sizes="96px"
        className="aspect-[4/5] w-24 shrink-0 self-start rounded-[4px]"
      />
    );
  }
  return (
    <div
      role="img"
      aria-label={title}
      className="grid aspect-[4/5] w-24 shrink-0 grid-cols-2 grid-rows-2 gap-0.5 self-start overflow-hidden rounded-[4px]"
    >
      {lines.slice(0, 4).map((line) => (
        <ProductImage key={line.id} image={line.merchandise.image} alt="" sizes="48px" className="size-full" />
      ))}
    </div>
  );
}

function LineRow({ line, onNavigate }: { line: CartLine; onNavigate?: () => void }) {
  const { updateLine, removeLines, pending } = useCart();
  const { product } = line.merchandise;
  const variant = variantLabel(line.merchandise.title);
  const message = attr(line.attributes, BOX_ATTR.message);

  return (
    <li className="flex gap-4 py-5">
      <Link href={`/products/${product.handle}`} onClick={onNavigate} className="shrink-0 self-start" tabIndex={-1} aria-hidden>
        <ProductImage image={line.merchandise.image} alt="" sizes="96px" className="aspect-[4/5] w-24 rounded-[4px]" />
      </Link>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-display text-xl leading-snug">
            <Link href={`/products/${product.handle}`} onClick={onNavigate} className="hover:underline">
              {product.title}
            </Link>
          </h3>
          <p className="shrink-0 tabular-nums">{formatMoney(line.cost.totalAmount)}</p>
        </div>
        {variant ? <p className="text-[0.9375rem] text-paper">{variant}</p> : null}
        {message ? (
          <p className="mt-2 line-clamp-3 border-l-2 border-gold/60 pl-3 font-serif text-[0.9375rem] italic text-paper">
            {message}
          </p>
        ) : null}
        <div className="mt-3 flex items-center gap-4">
          <QuantityStepper
            quantity={line.quantity}
            onChange={(quantity) => updateLine(line.id, quantity)}
            label={product.title}
            disabled={pending}
          />
          <button
            type="button"
            className="link text-sm disabled:opacity-50"
            onClick={() => removeLines([line.id])}
            disabled={pending}
          >
            Remove
          </button>
        </div>
      </div>
    </li>
  );
}
